import { weekDayCode } from '../../utils/utils';

type ProgramBudgetRule = {
  ACTIVE: boolean;
  BUDGET: number;
  SHIPPING_INCLUDED: boolean;
};

/**
 * A program's budget rule, plus the household-size variants nested under it
 * (`JP.HH2`, `LYNX-OTC.SFPB`, `BCMN-FEP['MH-HH5']`). No consumer reads a variant:
 * `foodUtils.ts:98` and `foodBundleItems.tsx:423` read `ACTIVE` and
 * `SHIPPING_INCLUDED` off the program level only, so the nested budgets are
 * configured but unused.
 */
type ProgramBudgetEntry = ProgramBudgetRule & {
  [variantCode: string]: ProgramBudgetRule | boolean | number;
};

/**
 * A vendor's default prices for one bundle name, or its `PRICE` fallback.
 *
 * `DEFAULT_FOOD_PRICE` is optional because `MISFITS` declares a shipping price and
 * no food price.
 */
export type VendorDefaultPrice = {
  DEFAULT_FOOD_PRICE?: number;
  DEFAULT_SHIPPING_PRICE: number;
};

export const VENDORS = {
  AB: 'AB',
  BM: 'BM',
  CCF: 'CCF',
  CFH: 'CFH',
  CJ: 'CJ',
  EW: 'EW',
  EF: 'EF',
  FF: 'FF',
  FFH: 'FFH',
  FFSH: 'FFSH',
  SSHE: 'SSHE',
  FL: 'FL',
  GB: 'GB',
  KB: 'KB',
  MF: 'MF',
  MH: 'MH',
  ML: 'ML',
  NM: 'NM',
  NU: 'NU',
  OK: 'OK',
  PK: 'PK',
  RK: 'RK',
  SB: 'SB',
  SS: 'SS',
  TF: 'TF',
  UM: 'UM',
  MM: 'MM',
  HH: 'HH',
  FRC: 'FRC',
};

/** The `type.coding` slice of a FHIR Organization that `isFoodCartVendorOrganization` reads. */
type FoodVendorOrganization = {
  type?: { coding?: { code?: string | undefined; system?: string | undefined }[] | undefined }[] | undefined;
};

/** CodeSystem for FHIR Organization food-vendor type codings. */
export const FOOD_VENDOR_TYPE_SYSTEM = 'https://projectwell.io/fhir/CodeSystem/food-vendor-type';

/** Organization type code for food-card vendors (shown on the FOOD CARD tab). */
export const FOOD_CART_VENDOR_TYPE_CODE = 'food-card';

/**
 * Returns true when a FHIR Organization resource is tagged as a food-card vendor.
 *
 * @param orgResource - FHIR Organization resource from `/Organization` search.
 */
export function isFoodCartVendorOrganization(orgResource: FoodVendorOrganization | undefined): boolean {
  return (
    orgResource?.type?.some((typeEntry) =>
      typeEntry?.coding?.some(
        (coding) => coding?.code === FOOD_CART_VENDOR_TYPE_CODE && coding?.system === FOOD_VENDOR_TYPE_SYSTEM
      )
    ) ?? false
  );
}

/** @deprecated Prefer `authInfo.foodCardVendors` from FHIR Organization type `food-card`. */
export const FOOD_CARD_VENDOR_IDS = [VENDORS.FRC];

export const PROGRAMS_MEQ = ['BCMN-MSHO-CHRO', 'HH-MCAID'];
export const PROGRAMS_BUDGET = ['WCNC-ARPA-PRX', 'HH-MCAID', 'BCMN-MSHO-CHRO'];

// Program budget is used to determine if the program has a budget limit
const PROGRAMS_BUDGET_MAP_LITERAL = {
  // JP: { BUDGET: 1053, SHIPPING_INCLUDED: true, ACTIVE: true },
  JP: {
    BUDGET: 663, // DEFAULT BUDGET
    SHIPPING_INCLUDED: true,
    ACTIVE: true,
    HH1: { BUDGET: 663, SHIPPING_INCLUDED: true, ACTIVE: true },
    HH2: { BUDGET: 847, SHIPPING_INCLUDED: true, ACTIVE: true },
    HH5: { BUDGET: 1032, SHIPPING_INCLUDED: true, ACTIVE: true },
  },
  'ASPI-SDOH-HC': { BUDGET: 321, SHIPPING_INCLUDED: true, ACTIVE: true },
  'CHC-ACT': { BUDGET: 480, SHIPPING_INCLUDED: true, ACTIVE: true },
  'WCNC-ARPA-PRX': { BUDGET: 960, SHIPPING_INCLUDED: false, ACTIVE: true },
  'HH-MCAID': { BUDGET: 680, SHIPPING_INCLUDED: true, ACTIVE: true },
  'BCMN-MSHO-CHRO': { BUDGET: 702, SHIPPING_INCLUDED: true, ACTIVE: true },
  'BCMN-PMAP-CHRO': {
    BUDGET: 622, // DEFAULT BUDGET
    SHIPPING_INCLUDED: true,
    ACTIVE: true,
    HH1: { BUDGET: 562, SHIPPING_INCLUDED: true, ACTIVE: true },
    HH2: { BUDGET: 602, SHIPPING_INCLUDED: true, ACTIVE: true },
    HH5: { BUDGET: 862, SHIPPING_INCLUDED: true, ACTIVE: true },
  },
  'BCMN-PMAP-MH': {
    BUDGET: 622, // DEFAULT BUDGET
    SHIPPING_INCLUDED: true,
    ACTIVE: true,
    HH1: { BUDGET: 622, SHIPPING_INCLUDED: true, ACTIVE: true },
    HH2: { BUDGET: 727, SHIPPING_INCLUDED: true, ACTIVE: true },
    HH5: { BUDGET: 862, SHIPPING_INCLUDED: true, ACTIVE: true },
  },
  'BCNC-EMPL-DM': { BUDGET: 900, SHIPPING_INCLUDED: true, ACTIVE: true },
  'BCNC-MCARE-DM': { BUDGET: 900, SHIPPING_INCLUDED: true, ACTIVE: true },
  'BCNC-IAFP-DM': { BUDGET: 900, SHIPPING_INCLUDED: true, ACTIVE: true },
  'BCNC-COMM-MH': { BUDGET: 560, SHIPPING_INCLUDED: true, ACTIVE: true },
  'BCNC-FEP': { BUDGET: 900, SHIPPING_INCLUDED: true, ACTIVE: true },
  'BCSC-MCARE-PD': { BUDGET: 80, SHIPPING_INCLUDED: false, ACTIVE: true },
  'BCSC-MCAID-MH': { BUDGET: 135, SHIPPING_INCLUDED: true, ACTIVE: true },
  'BCSC-MCAID': { BUDGET: 135, SHIPPING_INCLUDED: true, ACTIVE: true },
  'CFBC-MCAID': {
    BUDGET: 1476,
    SHIPPING_INCLUDED: true,
    ACTIVE: true,
    HH1: { BUDGET: 900, SHIPPING_INCLUDED: true, ACTIVE: true },
    HH2: { BUDGET: 1260, SHIPPING_INCLUDED: true, ACTIVE: true },
    HH5: { BUDGET: 1476, SHIPPING_INCLUDED: true, ACTIVE: true },
  },
  'SCHA-MCAID': { BUDGET: 892, SHIPPING_INCLUDED: true, ACTIVE: true },
  'TUFTS-WM-MTG': { BUDGET: 2460, SHIPPING_INCLUDED: true, ACTIVE: true },
  UHC: { BUDGET: 0, SHIPPING_INCLUDED: true, ACTIVE: false },
  'UHC-MCARE': { BUDGET: 180, SHIPPING_INCLUDED: true, ACTIVE: true },
  PYX: { BUDGET: 1000, SHIPPING_INCLUDED: true, ACTIVE: true },
  'IUH-BC': { BUDGET: 2160, SHIPPING_INCLUDED: true, ACTIVE: true },
  'IUH-CH': { BUDGET: 701, SHIPPING_INCLUDED: true, ACTIVE: true },
  'HOR-MCAID-MLTSS': { BUDGET: 1000, SHIPPING_INCLUDED: true, ACTIVE: true },
  'OPT-MA': { BUDGET: 378, SHIPPING_INCLUDED: true, ACTIVE: true },
  'CNT-ACA': { BUDGET: 675, SHIPPING_INCLUDED: true, ACTIVE: true },
  'NLA-DM': { BUDGET: 0, SHIPPING_INCLUDED: true, ACTIVE: false },
  'NLA-MH': { BUDGET: 1000, SHIPPING_INCLUDED: true, ACTIVE: true },
  'AH-MCAID-HOUSE': { BUDGET: 852, SHIPPING_INCLUDED: true, ACTIVE: true },
  'BCSC-MCAID-YOUTH': { BUDGET: 47, SHIPPING_INCLUDED: true, ACTIVE: true },
  'CNT-ACA-FI': { BUDGET: 130, SHIPPING_INCLUDED: true, ACTIVE: true },
  'LYNX-OTC': {
    BUDGET: 121, // DEFAULT BUDGET - Highest
    SHIPPING_INCLUDED: true,
    ACTIVE: true,
    '3MK': { BUDGET: 71, SHIPPING_INCLUDED: true, ACTIVE: true },
    '6MK': { BUDGET: 91, SHIPPING_INCLUDED: true, ACTIVE: true },
    '8MK': { BUDGET: 121, SHIPPING_INCLUDED: true, ACTIVE: true },
    SFPB: { BUDGET: 45, SHIPPING_INCLUDED: true, ACTIVE: true },
    LFPB: { BUDGET: 60, SHIPPING_INCLUDED: true, ACTIVE: true },
    ELFPB: { BUDGET: 70, SHIPPING_INCLUDED: true, ACTIVE: true },
    PSB: { BUDGET: 35, SHIPPING_INCLUDED: true, ACTIVE: true },
    BBPPS: { BUDGET: 70, SHIPPING_INCLUDED: true, ACTIVE: true },
    BBPPL: { BUDGET: 95, SHIPPING_INCLUDED: true, ACTIVE: true },
    BBPPAS: { BUDGET: 70, SHIPPING_INCLUDED: true, ACTIVE: true },
    BBPPAL: { BUDGET: 95, SHIPPING_INCLUDED: true, ACTIVE: true },
  },
  'EW-PAAS': {
    BUDGET: 2390,
    SHIPPING_INCLUDED: true,
    ACTIVE: true,
    'MA-RD': { BUDGET: 2390, SHIPPING_INCLUDED: true, ACTIVE: true },
    'MA-HC': { BUDGET: 2390, SHIPPING_INCLUDED: true, ACTIVE: true },
    'MA-MK': { BUDGET: 2390, SHIPPING_INCLUDED: true, ACTIVE: true },
  },
  'EW-HRSN-C3': {
    BUDGET: 2390,
    SHIPPING_INCLUDED: true,
    ACTIVE: true,
  },

  PC: {
    BUDGET: 1020,
    SHIPPING_INCLUDED: true,
    ACTIVE: true,
    HH1: { BUDGET: 1020, SHIPPING_INCLUDED: true, ACTIVE: true },
    'HALAL-HH1': { BUDGET: 1020, SHIPPING_INCLUDED: true, ACTIVE: true },
    'HALAL-HH2': { BUDGET: 1275, SHIPPING_INCLUDED: true, ACTIVE: true },
    HH5: { BUDGET: 1530, SHIPPING_INCLUDED: true, ACTIVE: true },
    'HALAL-HH5': { BUDGET: 1530, SHIPPING_INCLUDED: true, ACTIVE: true },
  },
  NRX: { BUDGET: 1000, SHIPPING_INCLUDED: true, ACTIVE: true },
  'NLA-FOOD': { BUDGET: 180, SHIPPING_INCLUDED: true, ACTIVE: true },
  HA: { BUDGET: 0, SHIPPING_INCLUDED: true, ACTIVE: false },
  HUMANA: { BUDGET: 0, SHIPPING_INCLUDED: true, ACTIVE: false },
  'BCMN-FEP': {
    BUDGET: 492, // DEFAULT BUDGET
    SHIPPING_INCLUDED: true,
    ACTIVE: true,
    'CM-HH1': { BUDGET: 492, SHIPPING_INCLUDED: true, ACTIVE: true },
    'CM-HH2-4': { BUDGET: 664, SHIPPING_INCLUDED: true, ACTIVE: true },
    'CM-HH5': { BUDGET: 739, SHIPPING_INCLUDED: true, ACTIVE: true },
    'MH-HH1': { BUDGET: 984, SHIPPING_INCLUDED: true, ACTIVE: true },
    'MH-HH2-4': { BUDGET: 1328, SHIPPING_INCLUDED: true, ACTIVE: true },
    'MH-HH5': { BUDGET: 1476, SHIPPING_INCLUDED: true, ACTIVE: true },
  },
  'DFH-MCAID': {
    BUDGET: 1257, // DEFAULT BUDGET - Highest (hh5-pmmk)
    SHIPPING_INCLUDED: true,
    ACTIVE: true,
    'HH1-PMGB': { BUDGET: 410, SHIPPING_INCLUDED: true, ACTIVE: true },
    'HH1-PMMK': { BUDGET: 414, SHIPPING_INCLUDED: true, ACTIVE: true },
    'HH2-4-PMGB': { BUDGET: 757, SHIPPING_INCLUDED: true, ACTIVE: true },
    'HH2-4-PMMK': { BUDGET: 747, SHIPPING_INCLUDED: true, ACTIVE: true },
    'HH5-PMGB': { BUDGET: 1104, SHIPPING_INCLUDED: true, ACTIVE: true },
    'HH5-PMMK': { BUDGET: 1257, SHIPPING_INCLUDED: true, ACTIVE: true },
  },
};

/*
 * Read only by runtime org (`props.org.toUpperCase()`), so the declaration needs
 * an index signature. This file's open-key pattern: `typeof LITERAL & Record<…>`
 * keeps a fixed-key read on its exact entry while a runtime-keyed read is
 * `… | undefined`, which is what an unlisted key yields.
 */
export const PROGRAMS_BUDGET_MAP: typeof PROGRAMS_BUDGET_MAP_LITERAL &
  Record<string, ProgramBudgetEntry | undefined> = PROGRAMS_BUDGET_MAP_LITERAL;

/**
 * Indexed with `referralType.toUpperCase()` in
 * `components/common/referrals/index.tsx`. An unlisted referral type yields
 * `undefined`, which the caller concatenates as the string 'undefined' into the
 * generated referral id.
 */
const REFERRAL_ID_PREFIX_LITERAL = { MAGNOLIA: 'MAG', 'SOUL FOOD': 'SOUL', SABOR: 'SAB' };
export const REFERRAL_ID_PREFIX: typeof REFERRAL_ID_PREFIX_LITERAL &
  Record<string, string | undefined> = REFERRAL_ID_PREFIX_LITERAL;

// Below pricing is not for Catalogs (only for Standard/Gap Orders)
// All Upper Case
/**
 * Default per-bundle pricing, keyed program → vendor → bundle.
 *
 * All three levels are indexed with computed keys in
 * `components/common/food/orders/add.tsx`; the inner reads are already written
 * as guarded `?.[…]?.` chains.
 */
const PROGRAM_VENDOR_CONFIG_LITERAL = {
  'BCNC-EMPL-DM': {
    'HUNGRY HARVEST': {
      'HRX STANDARD MINI': { DEFAULT_FOOD_PRICE: 25, DEFAULT_SHIPPING_PRICE: 0 },
      'HRX EASY TO EAT MINI': { DEFAULT_FOOD_PRICE: 25, DEFAULT_SHIPPING_PRICE: 0 },
      'HRX STANDARD FULL': { DEFAULT_FOOD_PRICE: 38, DEFAULT_SHIPPING_PRICE: 0 },
      'HRX EASY TO EAT FULL': { DEFAULT_FOOD_PRICE: 38, DEFAULT_SHIPPING_PRICE: 0 },
      PRICE: { DEFAULT_FOOD_PRICE: 0, DEFAULT_SHIPPING_PRICE: 0 },
    },
    'MOTHER EARTH': { PRICE: { DEFAULT_FOOD_PRICE: 24.99, DEFAULT_SHIPPING_PRICE: 8.99 } },
    'MODIFY HEALTH': { PRICE: { DEFAULT_FOOD_PRICE: 80.0, DEFAULT_SHIPPING_PRICE: 0 } },
    'BIGGERS MARKET': { PRICE: { DEFAULT_FOOD_PRICE: 31.0, DEFAULT_SHIPPING_PRICE: 0 } },
    KEBRIBAYAH: { PRICE: { DEFAULT_FOOD_PRICE: 80.43, DEFAULT_SHIPPING_PRICE: 18 } },
  },
  'BCNC-MCARE-DM': {
    'HUNGRY HARVEST': {
      'HRX STANDARD MINI': { DEFAULT_FOOD_PRICE: 25, DEFAULT_SHIPPING_PRICE: 0 },
      'HRX EASY TO EAT MINI': { DEFAULT_FOOD_PRICE: 25, DEFAULT_SHIPPING_PRICE: 0 },
      'HRX STANDARD FULL': { DEFAULT_FOOD_PRICE: 38, DEFAULT_SHIPPING_PRICE: 0 },
      'HRX EASY TO EAT FULL': { DEFAULT_FOOD_PRICE: 38, DEFAULT_SHIPPING_PRICE: 0 },
      PRICE: { DEFAULT_FOOD_PRICE: 0, DEFAULT_SHIPPING_PRICE: 0 },
    },
    'MOTHER EARTH': { PRICE: { DEFAULT_FOOD_PRICE: 24.99, DEFAULT_SHIPPING_PRICE: 8.99 } },
    'MODIFY HEALTH': { PRICE: { DEFAULT_FOOD_PRICE: 80.0, DEFAULT_SHIPPING_PRICE: 0 } },
    'BIGGERS MARKET': { PRICE: { DEFAULT_FOOD_PRICE: 31.0, DEFAULT_SHIPPING_PRICE: 0 } },
    KEBRIBAYAH: { PRICE: { DEFAULT_FOOD_PRICE: 80.43, DEFAULT_SHIPPING_PRICE: 18 } },
  },
  'BCNC-IAFP-DM': {
    'HUNGRY HARVEST': {
      'HRX STANDARD MINI': { DEFAULT_FOOD_PRICE: 25, DEFAULT_SHIPPING_PRICE: 0 },
      'HRX EASY TO EAT MINI': { DEFAULT_FOOD_PRICE: 25, DEFAULT_SHIPPING_PRICE: 0 },
      'HRX STANDARD FULL': { DEFAULT_FOOD_PRICE: 38, DEFAULT_SHIPPING_PRICE: 0 },
      'HRX EASY TO EAT FULL': { DEFAULT_FOOD_PRICE: 38, DEFAULT_SHIPPING_PRICE: 0 },
      PRICE: { DEFAULT_FOOD_PRICE: 0, DEFAULT_SHIPPING_PRICE: 0 },
    },
    'MOTHER EARTH': { PRICE: { DEFAULT_FOOD_PRICE: 24.99, DEFAULT_SHIPPING_PRICE: 8.99 } },
    'MODIFY HEALTH': { PRICE: { DEFAULT_FOOD_PRICE: 80.0, DEFAULT_SHIPPING_PRICE: 0 } },
    'BIGGERS MARKET': { PRICE: { DEFAULT_FOOD_PRICE: 31.0, DEFAULT_SHIPPING_PRICE: 0 } },
    KEBRIBAYAH: { PRICE: { DEFAULT_FOOD_PRICE: 80.43, DEFAULT_SHIPPING_PRICE: 18 } },
  },
  'BCNC-FE-DM': {
    'HUNGRY HARVEST': {
      'HRX STANDARD MINI': { DEFAULT_FOOD_PRICE: 25, DEFAULT_SHIPPING_PRICE: 0 },
      'HRX EASY TO EAT MINI': { DEFAULT_FOOD_PRICE: 25, DEFAULT_SHIPPING_PRICE: 0 },
      'HRX STANDARD FULL': { DEFAULT_FOOD_PRICE: 38, DEFAULT_SHIPPING_PRICE: 0 },
      'HRX EASY TO EAT FULL': { DEFAULT_FOOD_PRICE: 38, DEFAULT_SHIPPING_PRICE: 0 },
      PRICE: { DEFAULT_FOOD_PRICE: 0, DEFAULT_SHIPPING_PRICE: 0 },
    },
    'MOTHER EARTH': { PRICE: { DEFAULT_FOOD_PRICE: 24.99, DEFAULT_SHIPPING_PRICE: 8.99 } },
    'MODIFY HEALTH': { PRICE: { DEFAULT_FOOD_PRICE: 80.0, DEFAULT_SHIPPING_PRICE: 0 } },
    'BIGGERS MARKET': { PRICE: { DEFAULT_FOOD_PRICE: 31.0, DEFAULT_SHIPPING_PRICE: 0 } },
    KEBRIBAYAH: { PRICE: { DEFAULT_FOOD_PRICE: 80.43, DEFAULT_SHIPPING_PRICE: 18 } },
  },
  'BCNC-COMM-MH': {
    'HUNGRY HARVEST': {
      'HRX STANDARD MINI': { DEFAULT_FOOD_PRICE: 25, DEFAULT_SHIPPING_PRICE: 0 },
      'HRX EASY TO EAT MINI': { DEFAULT_FOOD_PRICE: 25, DEFAULT_SHIPPING_PRICE: 0 },
      'HRX STANDARD FULL': { DEFAULT_FOOD_PRICE: 38, DEFAULT_SHIPPING_PRICE: 0 },
      'HRX EASY TO EAT FULL': { DEFAULT_FOOD_PRICE: 38, DEFAULT_SHIPPING_PRICE: 0 },
      PRICE: { DEFAULT_FOOD_PRICE: 0, DEFAULT_SHIPPING_PRICE: 0 },
    },
    'MOTHER EARTH': { PRICE: { DEFAULT_FOOD_PRICE: 24.99, DEFAULT_SHIPPING_PRICE: 8.99 } },
    'MODIFY HEALTH': { PRICE: { DEFAULT_FOOD_PRICE: 80.0, DEFAULT_SHIPPING_PRICE: 0 } },
    'BIGGERS MARKET': { PRICE: { DEFAULT_FOOD_PRICE: 31.0, DEFAULT_SHIPPING_PRICE: 0 } },
    KEBRIBAYAH: { PRICE: { DEFAULT_FOOD_PRICE: 80.43, DEFAULT_SHIPPING_PRICE: 18 } },
  },
  //  SSHE: ['10-Meal Bundle', '7d meal plan', '4d meal plan'],
  DEFAULT: {
    'HUNGRY HARVEST': {
      'HRX STANDARD MINI': { DEFAULT_FOOD_PRICE: 25, DEFAULT_SHIPPING_PRICE: 0 },
      'HRX EASY TO EAT MINI': { DEFAULT_FOOD_PRICE: 25, DEFAULT_SHIPPING_PRICE: 0 },
      'HRX STANDARD FULL': { DEFAULT_FOOD_PRICE: 38, DEFAULT_SHIPPING_PRICE: 0 },
      'HRX EASY TO EAT FULL': { DEFAULT_FOOD_PRICE: 38, DEFAULT_SHIPPING_PRICE: 0 },
      PRICE: { DEFAULT_FOOD_PRICE: 0, DEFAULT_SHIPPING_PRICE: 0 },
    },
    'MORRISSEY MARKET': {
      'GROCERY BUNDLE CT': { DEFAULT_FOOD_PRICE: 63, DEFAULT_SHIPPING_PRICE: 0 },
    },
    UMOJA: {
      'PANTRY BUNDLE A ($75)': { DEFAULT_FOOD_PRICE: 75, DEFAULT_SHIPPING_PRICE: 0 },
      'PRODUCE BUNDLE ($60)': { DEFAULT_FOOD_PRICE: 60, DEFAULT_SHIPPING_PRICE: 0 },
      'PROTEIN BUNDLE A ($70)': { DEFAULT_FOOD_PRICE: 70, DEFAULT_SHIPPING_PRICE: 0 },
      'SNACK BUNDLE A ($45)': { DEFAULT_FOOD_PRICE: 45, DEFAULT_SHIPPING_PRICE: 0 },
      'PANTRY BUNDLE B ($60)': { DEFAULT_FOOD_PRICE: 60, DEFAULT_SHIPPING_PRICE: 0 },
      'SNACK BUNDLE B ($65)': { DEFAULT_FOOD_PRICE: 65, DEFAULT_SHIPPING_PRICE: 0 },
      'PROTEIN BUNDLE B ($60)': { DEFAULT_FOOD_PRICE: 60, DEFAULT_SHIPPING_PRICE: 0 },
      PRICE: { DEFAULT_FOOD_PRICE: 0, DEFAULT_SHIPPING_PRICE: 0 },
    },
    SSHE: {
      '10 MEAL BUNDLE': { DEFAULT_FOOD_PRICE: 136, DEFAULT_SHIPPING_PRICE: 10.99 },
      '7D MEAL PLAN 2K': { DEFAULT_FOOD_PRICE: 193, DEFAULT_SHIPPING_PRICE: 24 },
      '4D MEAL PLAN 2K': { DEFAULT_FOOD_PRICE: 124, DEFAULT_SHIPPING_PRICE: 12 },
      PRICE: { DEFAULT_FOOD_PRICE: 0, DEFAULT_SHIPPING_PRICE: 0 },
    },
    // FFSH setup as catalog, below options are not needed for now
    FFSH: {
      'MAGNOLIA (VEG) - MED': { DEFAULT_FOOD_PRICE: 30, DEFAULT_SHIPPING_PRICE: 0 },
      'SOUL FOOD - MED': { DEFAULT_FOOD_PRICE: 30, DEFAULT_SHIPPING_PRICE: 0 },
      'SABOR - MED': { DEFAULT_FOOD_PRICE: 30, DEFAULT_SHIPPING_PRICE: 0 },
      'MAGNOLIA (VEG) - LG': { DEFAULT_FOOD_PRICE: 50, DEFAULT_SHIPPING_PRICE: 0 },
      'SOUL FOOD - LG': { DEFAULT_FOOD_PRICE: 50, DEFAULT_SHIPPING_PRICE: 0 },
      'SABOR - LG': { DEFAULT_FOOD_PRICE: 50, DEFAULT_SHIPPING_PRICE: 0 },
      PRICE: { DEFAULT_FOOD_PRICE: 0, DEFAULT_SHIPPING_PRICE: 7.95 },
    },
    KEBRIBAYAH: { PRICE: { DEFAULT_FOOD_PRICE: 80.43, DEFAULT_SHIPPING_PRICE: 18 } },
    // SSHE: { PRICE: { DEFAULT_FOOD_PRICE: 136.0, DEFAULT_SHIPPING_PRICE: 10.99 } },
    HONESTMED: { PRICE: { DEFAULT_FOOD_PRICE: 89.99, DEFAULT_SHIPPING_PRICE: 0 } },
    'FRESH LIST': { PRICE: { DEFAULT_FOOD_PRICE: 35.0, DEFAULT_SHIPPING_PRICE: 0 } },
    "RAM'S KITCHEN": { PRICE: { DEFAULT_FOOD_PRICE: 80, DEFAULT_SHIPPING_PRICE: 0 } },
    MWUYF: {
      'PRODUCE ONLY': { DEFAULT_FOOD_PRICE: 35, DEFAULT_SHIPPING_PRICE: 0 },
      // 'Produce A + Pantry A': { DEFAULT_FOOD_PRICE: 80, DEFAULT_SHIPPING_PRICE: 0 },
      // 'Produce A + Pantry B': { DEFAULT_FOOD_PRICE: 80, DEFAULT_SHIPPING_PRICE: 0 },
      // 'Produce B + Pantry A': { DEFAULT_FOOD_PRICE: 80, DEFAULT_SHIPPING_PRICE: 0 },
      // 'Produce B + Pantry B': { DEFAULT_FOOD_PRICE: 80, DEFAULT_SHIPPING_PRICE: 0 },
      PRICE: { DEFAULT_FOOD_PRICE: 80, DEFAULT_SHIPPING_PRICE: 0 },
    },
    'BIGGERS MARKET': { PRICE: { DEFAULT_FOOD_PRICE: 31.0, DEFAULT_SHIPPING_PRICE: 0 } },
    MISFITS: { PRICE: { DEFAULT_SHIPPING_PRICE: 8.85 } },
  },
} satisfies Record<string, Record<string, Record<string, VendorDefaultPrice>>>;

/**
 * The `satisfies` above adds no index signature, so the intersection is what
 * makes `PROGRAM_VENDOR_CONFIG?.[props.org]` legal and `… | undefined` — what an
 * unlisted program yields, and what the `add.tsx` `?.` chains expect.
 */
export const PROGRAM_VENDOR_CONFIG: typeof PROGRAM_VENDOR_CONFIG_LITERAL &
  Record<string, Record<string, Record<string, VendorDefaultPrice>> | undefined> =
  PROGRAM_VENDOR_CONFIG_LITERAL;

export const NON_LOCAL_VENDORS = [
  'MISFITS',
  'SUN BASKET',
  'PERFORMANCE KITCHEN',
  'NOURISH MEALS',
  'INSTACART',
  'UBER EATS',
];

/** Whether a vendor's orders may be future-dated and whether a cutoff applies. */
export type VendorOrderPrivilege = { CutOffDate: boolean; FutureDate: boolean };

const VENDORS_ORDERS_PRIVILEGE_LITERAL = {
  PK: {
    FutureDate: false,
    CutOffDate: false,
  },
  SB: {
    FutureDate: false,
    CutOffDate: true,
  },
  MF: {
    FutureDate: false,
    CutOffDate: false,
  },
  AB: {
    FutureDate: false,
    CutOffDate: false,
  },
  FF: {
    FutureDate: false,
    CutOffDate: false,
  },
  LK: {
    FutureDate: false,
    CutOffDate: false,
  },
  GP: {
    FutureDate: false,
    CutOffDate: false,
  },
  FY: {
    FutureDate: false,
    CutOffDate: false,
  },
  JF: {
    FutureDate: false,
    CutOffDate: false,
  },
  GD: {
    FutureDate: false,
    CutOffDate: false,
  },
  IF: {
    FutureDate: false,
    CutOffDate: false,
  },
  GB: {
    FutureDate: false,
    CutOffDate: false,
  },
  VT: {
    FutureDate: false,
    CutOffDate: false,
  },
  SS: {
    FutureDate: false,
    CutOffDate: false,
  },
  TN: {
    FutureDate: false,
    CutOffDate: false,
  },
  TF: {
    FutureDate: false,
    CutOffDate: false,
  },
  AM: {
    FutureDate: false,
    CutOffDate: false,
  },
  NM: {
    FutureDate: false,
    CutOffDate: false,
  },
  OK: {
    FutureDate: false,
    CutOffDate: false,
  },
  MH: {
    FutureDate: false,
    CutOffDate: false,
  },
  FL: {
    FutureDate: false,
    CutOffDate: false,
  },
  BM: { FutureDate: false, CutOffDate: false },
  EF: {
    FutureDate: false,
    CutOffDate: false,
  },
  EW: {
    FutureDate: false,
    CutOffDate: false,
  },
  KB: {
    FutureDate: false,
    CutOffDate: false,
  },
  ML: {
    FutureDate: false,
    CutOffDate: false,
  },
  CFH: {
    FutureDate: false,
    CutOffDate: false,
  },
  CJ: { FutureDate: false, CutOffDate: false },
  FFSH: {
    FutureDate: false,
    CutOffDate: false,
  },
  SSHE: {
    FutureDate: false,
    CutOffDate: false,
  },
  NU: {
    FutureDate: false,
    CutOffDate: false,
  },
  FFH: {
    FutureDate: false,
    CutOffDate: false,
  },
  CCF: {
    FutureDate: false,
    CutOffDate: false,
  },
  MM: {
    FutureDate: false,
    CutOffDate: false,
  },
  HH: {
    FutureDate: false,
    CutOffDate: false,
  },
} satisfies Record<string, VendorOrderPrivilege>;

/*
 * Read only by runtime vendor code (`props.vendor`) in
 * `components/common/food/catalog/item/foodBundleItems.tsx`, so the declaration needs
 * an index signature. This file's open-key pattern: `typeof LITERAL & Record<…>` keeps
 * a fixed-key read on its exact entry while a runtime-keyed read is `… | undefined`,
 * which is what an unlisted vendor yields.
 */
export const VENDORS_ORDERS_PRIVILEGE: typeof VENDORS_ORDERS_PRIVILEGE_LITERAL &
  Record<string, VendorOrderPrivilege | undefined> = VENDORS_ORDERS_PRIVILEGE_LITERAL;

export const MF_SCHEMA = [
  { column: 'First Name', attribute: 'firstName' },
  { column: 'Last Name', attribute: 'lastName' },
  { column: 'Line One', attribute: 'delivery_addressLine1' },
  { column: 'Line Two', attribute: 'delivery_addressLine2' },
  { column: 'City', attribute: 'delivery_city' },
  { column: 'State', attribute: 'delivery_state' },
  { column: 'deliveryAddress.zipcode', attribute: 'delivery_zip' },
  { column: 'Email', attribute: 'email' },
  { column: 'Phone', attribute: 'homePhone' },
  { column: 'Curated List Name', attribute: 'bundleId' },
  { column: 'Day', value: '' },
  { column: 'Source', value: 'Nourish_RX' },
  { column: 'BlockComms(Y/N)', value: 'Y' },
  { column: 'Notes', attribute: 'patientId' },
];

export const UM_SCHEMA = [
  { column: 'Recipient_ID', attribute: 'patientId' },
  { column: 'Program_ID', attribute: 'patientId' },
  { column: 'Order_Date', attribute: 'orderDate' },
  { column: 'SKU', attribute: 'orderName' },
  { column: 'Frequency_Unit', attribute: 'Single' },
  { column: 'Frequency_Qty', attribute: '1' },
  { column: 'Frequency_Repeat', attribute: 'Single' },
  { column: 'Preferred_DOW', attribute: '' },
  { column: 'First_Name', attribute: 'firstName' },
  { column: 'Last_Name', attribute: 'lastName' },
  { column: 'Address_1', attribute: 'delivery_addressLine1' },
  { column: 'Address_2', attribute: 'delivery_addressLine2' },
  { column: 'City', attribute: 'delivery_city' },
  { column: 'State', attribute: 'delivery_state' },
  { column: 'Zip', attribute: 'delivery_zip' },
  { column: 'Phone', attribute: 'homePhone' },
  { column: 'Status', attribute: 'Active' },
  { column: 'Special Instructions', attribute: 'deliveryInstructions' },
];

export const FF_SCHEMA = [
  { column: 'Participant ID', attribute: 'patientId' },
  { column: 'Participant Allergies', value: 'allergies' },
  { column: 'First Name', attribute: 'firstName' },
  { column: 'Last Name', attribute: 'lastName' },
  { column: 'Street Address', attribute: 'delivery_addressLine1+delivery_city' },
  { column: 'Zip Code', attribute: 'delivery_zip' },
  { column: 'State', attribute: 'delivery_state' },
  { column: 'Country', value: 'United States (US)' },
  { column: 'Delivery Notes', value: '' },
  { column: 'Order Type', value: 'Delivery' },
  { column: 'Phone', attribute: 'homePhone' },
  { column: 'Meal', attribute: 'itemName' },
  { column: 'Qty', attribute: 'quantity' },
];

export const AB_SCHEMA = [
  { column: 'storeExternalId', attribute: '' },
  { column: 'orderExternalId', attribute: 'orderId' },
  { column: 'type', value: '' },
  { column: 'orderValue', value: '' },
  { column: 'dispatch.type', value: '' },
  { column: 'dispatch.time', value: '' },
  { column: 'dispatch.basedOn', value: '' },
  { column: 'pickupTime.startsAt', value: '' },
  { column: 'pickupTime.endsAt', value: '' },
  { column: 'dropoffTime.startsAt', value: '' },
  { column: 'dropoffTime.endsAt', value: '' },
  { column: 'deliveryContact.name', attribute: 'firstName+lastName' },
  { column: 'deliveryContact.phone', attribute: 'homePhone' },
  { column: 'deliveryContact.customerId', value: '' },
  { column: 'deliveryContact.email', attribute: 'email' },
  { column: 'deliveryAddress.street', attribute: 'delivery_addressLine1' },
  { column: 'deliveryAddress.street2', attribute: 'delivery_addressLine2' },
  { column: 'deliveryAddress.city', attribute: 'delivery_city' },
  { column: 'deliveryAddress.state', attribute: 'delivery_state' },
  { column: 'deliveryAddress.country', value: 'US' },
  { column: 'deliveryAddress.zipcode', attribute: 'delivery_zip' },
  { column: 'deliveryAddress.latitude', value: '' },
  { column: 'deliveryAddress.longitude', value: '' },
  { column: 'deliveryAddress.apartmentNumber', value: '' },
  { column: 'packages.0.name', value: '' },
  { column: 'packages.0.quantity', value: '' },
  { column: 'packages.0.items', value: '' },
  { column: 'packages.0.description', value: '' },
  { column: 'packages.0.weight', value: '' },
  { column: 'packages.0.size.length', value: '' },
  { column: 'packages.0.size.width', value: '' },
  { column: 'packages.0.size.height', value: '' },
  { column: 'labelAttributes.pageSize.length', value: '' },
  { column: 'labelAttributes.pageSize.width', value: '' },
  { column: 'isSpirit', value: '' },
  { column: 'isBeerOrWine', value: '' },
  { column: 'isTobacco', value: '' },
  { column: 'isRx', value: '' },
  { column: 'isFragile', value: '' },
  { column: 'hasRefrigeratedItems', value: '' },
  { column: 'hasPerishableItems', value: '' },
  { column: 'notifications.url', value: '' },
  { column: 'notifications.sms.0', value: '' },
  { column: 'notifications.email.0', value: '' },
  { column: 'signature', value: '' },
  { column: 'tip', value: '' },
  { column: 'deliveryInstructions', value: '' },
  { column: 'pickupInstructions', value: '' },
  { column: 'deliveryContact.notifySms', value: '' },
  { column: 'deliveryContact.notifyEmail', value: '' },
  { column: 'alternateLocation.address.street', value: '' },
  { column: 'alternateLocation.address.street2', value: '' },
  { column: 'alternateLocation.address.city', value: '' },
  { column: 'alternateLocation.address.state', value: '' },
  { column: 'alternateLocation.address.country', value: '' },
  { column: 'alternateLocation.address.zipcode', value: '' },
  { column: 'alternateLocation.address.latitude', value: '' },
  { column: 'alternateLocation.address.longitude', value: '' },
  { column: 'alternateLocation.distance.value', value: '' },
  { column: 'alternateLocation.distance.unit', value: '' },
  { column: 'alternateLocation.phone', value: '' },
  { column: 'alternateLocation.dspLocationId', value: '' },
  { column: 'alternateLocation.name', value: '' },
  { column: 'alternateLocation.locationTimings.0', value: '' },
  { column: 'alternateLocation.locationTags.0', value: '' },
  { column: 'alternateLocation.provider', value: '' },
  { column: 'Bundle 1', attribute: 'bundleName' },
  { column: 'Bundle 2', attribute: '' },
  { column: 'Bundle 3', attribute: '' },
];

/**
 * Per-vendor order-cutoff state. 1 is Monday.
 *
 * `cutOffDay` admits `undefined` because `weekDayCode` in `utils/utils` has no
 * `default` arm; the `''` seed on every vendor without a cutoff is why `string` is in
 * the union.
 */
export type VendorOrderCutoff = {
  cutOffDay: number | string | undefined;
  flag: boolean;
  message: string;
};

/**
 * `CheckOrderCutoff` and `GetTasksStartDate` in `./vendorUtils` assign `flag` and
 * `message` onto these entries and hand the whole table back, so entries must stay
 * writable while the table keeps its literal keys for their dotted reads.
 */
const cutoff = (entry: VendorOrderCutoff): VendorOrderCutoff => entry;

const VENDORS_ORDER_CUTOFFDAY_LITERAL = {
  PK: cutoff({ flag: false, message: '', cutOffDay: '' }),
  SB: cutoff({ flag: false, message: '', cutOffDay: '' }),
  FF: cutoff({ flag: false, message: '', cutOffDay: '' }),
  GB: cutoff({ flag: false, message: '', cutOffDay: '' }),
  NM: cutoff({ flag: false, message: '', cutOffDay: '' }),
  UM: cutoff({ flag: false, message: '', cutOffDay: '' }),
  MF: cutoff({ flag: false, message: '', cutOffDay: weekDayCode('TUESDAY') }),
  AB: cutoff({ flag: false, message: '', cutOffDay: weekDayCode('THURSDAY') }),
  OK: cutoff({ flag: false, message: '', cutOffDay: '' }),
  MH: cutoff({ flag: false, message: '', cutOffDay: '' }),
  FL: cutoff({ flag: false, message: '', cutOffDay: '' }),
  BM: cutoff({ flag: false, message: '', cutOffDay: '' }),
  EF: cutoff({ flag: false, message: '', cutOffDay: '' }),
  EW: cutoff({ flag: false, message: '', cutOffDay: '' }),
  ML: cutoff({ flag: false, message: '', cutOffDay: '' }),
  CFH: cutoff({ flag: false, message: '', cutOffDay: '' }),
  CJ: cutoff({ flag: false, message: '', cutOffDay: '' }),

  FFH: cutoff({ flag: false, message: '', cutOffDay: '' }),
  FFSH: cutoff({ flag: false, message: '', cutOffDay: '' }),
  SSHE: cutoff({ flag: false, message: '', cutOffDay: '' }),
  NU: cutoff({ flag: false, message: '', cutOffDay: '' }),
  CCF: cutoff({ flag: false, message: '', cutOffDay: '' }),
  MM: cutoff({ flag: false, message: '', cutOffDay: '' }),
  HH: cutoff({ flag: false, message: '', cutOffDay: '' }),
};

/*
 * Indexed by runtime vendor code in
 * `components/common/food/catalog/item/foodBundleItems.tsx` while `./vendorUtils` reads
 * fixed keys off it (`.MF.cutOffDay`, `.AB.cutOffDay`). The open-key pattern serves
 * both: the dotted reads stay exact, and a runtime-keyed read is `… | undefined`, which
 * is what an unlisted vendor yields.
 */
export const VENDORS_ORDER_CUTOFFDAY: typeof VENDORS_ORDER_CUTOFFDAY_LITERAL &
  Record<string, VendorOrderCutoff | undefined> = VENDORS_ORDER_CUTOFFDAY_LITERAL;

/** Inverts a vendor dropdown's options into a code-to-display-name lookup. */
export const VENDOR_MAP = (array: { text: string; value: string }[]): Record<string, string> => {
  return array.reduce<Record<string, string>>((result, option) => {
    result[option.value] = option.text;
    return result;
  }, {});
};
